import { pb } from "@/lib/pocketbase";
import { Users } from "@/types/pocketbase-types";
import posthog from "posthog-js";
import { AuthModel } from "pocketbase";

export const isValidSession = () => {
  return pb.authStore.isValid;
};

export const logout = () => {
  pb.authStore.clear();

  // Reset the PostHog identity when user logs out
  posthog.reset();
};

export const login = async (email: string, password: string) => {
  const authData = await pb
    .collection("users")
    .authWithPassword(email, password);

  // Identify user in PostHog after successful login
  if (authData.record) {
    identifyUserInPosthog(authData.record);
  }
};

/**
 * Identifies the user in PostHog with available user data
 * @param user The authenticated user record
 */
export const identifyUserInPosthog = async (user: AuthModel | null) => {
  // If user is null, we can't identify them
  if (!user) {
    console.warn("Cannot identify null user in PostHog");
    return;
  }

  // Identify user
  posthog.identify(
    user.id, // Use the user's unique ID as the distinct_id
    {
      email: user.email,
      name: user.name,
      created: user.created,
      org_id: user.org,
    },
    {
      email: user.email,
      name: user.name,
      created: user.created,
      org_id: user.org,
    },
  );
};

export const sendVerificationEmail = async (email: string) => {
  await pb.collection("users").requestVerification(email);
};

export interface UTMParams {
  utm_source?: string;
  utm_medium?: string;
  utm_campaign?: string;
  utm_content?: string;
  utm_term?: string;
}

export const register = async (
  name: string,
  email: string,
  password: string,
  passwordConfirm: string,
  utmParams?: UTMParams,
) => {
  const res = await pb.collection("users").create<Users>({
    name,
    email,
    password,
    passwordConfirm,
    emailVisibility: true,
    isAdmin: true,
    role: "admin",
    ...utmParams,
  });
  await sendVerificationEmail(email);
  return res;
};

export const resetPassword = async (email: string) => {
  await pb.collection("users").requestPasswordReset(email);
};

export const confirmPasswordReset = async (
  token: string,
  password: string,
  passwordConfirm: string,
) => {
  await pb
    .collection("users")
    .confirmPasswordReset(token, password, passwordConfirm);
};

/**
 * Authenticates a user with Google OAuth2
 * @param preOpenedPopup An optional pre-opened popup window to use for authentication
 * @returns The authentication data if successful
 */
export const loginWithGoogle = async (preOpenedPopup?: Window | null) => {
  // Get UTM params from sessionStorage if they exist
  let createData = {};
  const storedUtmParams = sessionStorage.getItem("utmParams");
  if (storedUtmParams) {
    try {
      const utmParams = JSON.parse(storedUtmParams);
      createData = {
        utm_source: utmParams.utm_source || "",
        utm_medium: utmParams.utm_medium || "",
        utm_campaign: utmParams.utm_campaign || "",
        utm_content: utmParams.utm_content || "",
        utm_term: utmParams.utm_term || "",
      };
    } catch (error) {
      console.error("Failed to parse UTM parameters:", error);
    }
  }

  const authData = await pb.collection("users").authWithOAuth2({
    provider: "google",
    createData, // Pass UTM params as createData for new signups
    urlCallback: (url) => {
      // If a popup was pre-opened, use it; otherwise open a new one
      let popup = preOpenedPopup;

      if (popup) {
        // Navigate the pre-opened popup to the OAuth URL
        popup.location.href = url;
      } else {
        // Fall back to opening a new popup if none was provided
        popup = window.open(
          url,
          "oauth",
          "height=600,width=800,status=yes,toolbar=no,menubar=no,location=no",
        );
      }

      if (!popup) {
        throw new Error(
          "Failed to open the authentication window. Please allow popups for this site.",
        );
      }
    },
  });

  // Identify user in PostHog after successful login
  if (authData.record) {
    identifyUserInPosthog(authData.record);

    // Clear the stored UTM params after successful authentication
    if (storedUtmParams) {
      sessionStorage.removeItem("utmParams");
    }
  }

  return authData;
};

// (connectGoogleSearchConsole removed; use initiateGSCAuth from gsc.ts instead)
